class CaratWeightRange extends HTMLElement {
  constructor() {
    super();
    this.rangeLowerBound = null;
    this.rangeHigherBound = null;
    this.textInputLowerBound = null;
    this.textInputHigherBound = null;
    this.debounceTimer = null;
    this.loadingIndicator = null;
  }

  connectedCallback() {
    try {
      this.rangeLowerBound = this.querySelector('input[type="range"]:first-child');
      this.rangeHigherBound = this.querySelector('input[type="range"]:last-child');
      this.textInputLowerBound = this.querySelector('#filter_min_carat_weight');
      this.textInputHigherBound = this.querySelector('#filter_max_carat_weight');

      if (!this.rangeLowerBound || !this.rangeHigherBound || !this.textInputLowerBound || !this.textInputHigherBound) {
        throw new Error('Required elements not found in CaratWeightRange');
      }

      this.createLoadingIndicator();
      this.setupEventListeners();
      this.updateRangeStyles();
    } catch (error) {
      console.error('Error initializing CaratWeightRange:', error);
    }
  }

  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.textContent = window.Shopify?.locale === 'de' ? 'Aktualisiere...' : 'Updating...';
    this.loadingIndicator.style.display = 'none';
    this.loadingIndicator.style.position = 'absolute';
    this.loadingIndicator.style.top = '100%';
    this.loadingIndicator.style.left = '50%';
    this.loadingIndicator.style.transform = 'translateX(-50%)';
    this.loadingIndicator.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    this.loadingIndicator.style.color = 'white';
    this.loadingIndicator.style.padding = '5px 10px';
    this.loadingIndicator.style.borderRadius = '3px';
    this.loadingIndicator.style.fontSize = '12px';
    this.appendChild(this.loadingIndicator);
  }

  setupEventListeners() {
    this.textInputLowerBound.addEventListener("focus", () => this.textInputLowerBound.select());
    this.textInputHigherBound.addEventListener("focus", () => this.textInputHigherBound.select());

    this.textInputLowerBound.addEventListener("change", (event) => this.handleTextInputChange(event, true));
    this.textInputHigherBound.addEventListener("change", (event) => this.handleTextInputChange(event, false));

    this.rangeLowerBound.addEventListener("input", (event) => this.handleRangeInput(event, this.textInputLowerBound, '--range-min'));
    this.rangeHigherBound.addEventListener("input", (event) => this.handleRangeInput(event, this.textInputHigherBound, '--range-max'));

    ["mouseup", "touchend", "change"].forEach(eventType => {
      this.rangeLowerBound.addEventListener(eventType, (event) => this.handleRangeChange(event, this.textInputLowerBound));
      this.rangeHigherBound.addEventListener(eventType, (event) => this.handleRangeChange(event, this.textInputHigherBound));
    });

    // Add keyboard accessibility
    this.rangeLowerBound.addEventListener("keydown", (event) => this.handleRangeKeydown(event, this.textInputLowerBound));
    this.rangeHigherBound.addEventListener("keydown", (event) => this.handleRangeKeydown(event, this.textInputHigherBound));
  }

  handleTextInputChange(event, isLower) {
    try {
      event.preventDefault();
      const value = parseFloat(event.target.value);
      const closestValue = this.findClosestValue(value);

      if (isLower) {
        const maxValue = parseFloat(this.textInputHigherBound.value) - parseFloat(this.rangeLowerBound.step);
        const boundedValue = Math.max(Math.min(closestValue, maxValue), parseFloat(this.rangeLowerBound.min));
        this.textInputLowerBound.value = boundedValue;
        this.rangeLowerBound.value = boundedValue;
        this.updateRangeStyle(this.rangeLowerBound, '--range-min');
      } else {
        const minValue = parseFloat(this.textInputLowerBound.value) + parseFloat(this.rangeHigherBound.step);
        const boundedValue = Math.min(Math.max(closestValue, minValue), parseFloat(this.rangeHigherBound.max));
        this.textInputHigherBound.value = boundedValue;
        this.rangeHigherBound.value = boundedValue;
        this.updateRangeStyle(this.rangeHigherBound, '--range-max');
      }

      this.updateCheckboxes();
    } catch (error) {
      console.error('Error in handleTextInputChange:', error);
    }
  }

  handleRangeInput(event, textInput, cssVar) {
    try {
      const closestValue = this.findClosestValue(parseFloat(event.target.value));
      event.target.value = closestValue;
      this.updateRangeStyle(event.target, cssVar);
      textInput.value = closestValue;
    } catch (error) {
      console.error('Error in handleRangeInput:', error);
    }
  }

  handleRangeChange(event, textInput) {
    try {
      event.stopPropagation();
      textInput.value = event.target.value;
      textInput.dispatchEvent(new Event("change", { bubbles: true }));
    } catch (error) {
      console.error('Error in handleRangeChange:', error);
    }
  }

  handleRangeKeydown(event, textInput) {
    const step = parseFloat(event.target.step) || 1;
    let newValue;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = parseFloat(event.target.value) + step;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = parseFloat(event.target.value) - step;
        break;
      default:
        return;
    }

    event.preventDefault();
    event.target.value = this.findClosestValue(newValue);
    this.handleRangeChange(event, textInput);
  }

  updateRangeStyle(rangeElement, cssVar) {
    try {
      const progress = (parseFloat(rangeElement.value) - parseFloat(rangeElement.min)) / (parseFloat(rangeElement.max) - parseFloat(rangeElement.min)) * 100;
      rangeElement.parentElement.style.setProperty(cssVar, `${progress}%`);
    } catch (error) {
      console.error('Error in updateRangeStyle:', error);
    }
  }

  updateRangeStyles() {
    this.updateRangeStyle(this.rangeLowerBound, '--range-min');
    this.updateRangeStyle(this.rangeHigherBound, '--range-max');
  }

  findClosestValue(value) {
    if (!window.filterValues || !Array.isArray(window.filterValues)) {
      console.error('filterValues is not properly defined');
      return value;
    }
    return window.filterValues.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  }

  updateCheckboxes() {
    try {
      const checkboxes = document.querySelectorAll('input[type="checkbox"][name="filter.p.m.custom.carat_weight"]');
      checkboxes.forEach(checkbox => {
        const value = parseFloat(checkbox.value);
        checkbox.checked = value >= parseFloat(this.textInputLowerBound.value) &&
          value <= parseFloat(this.textInputHigherBound.value);
      });

      const form = checkboxes[0]?.closest('form');
      if (form) {
        this.debounceFormSubmit(form);
      } else {
        console.error('Form not found');
      }
    } catch (error) {
      console.error('Error in updateCheckboxes:', error);
    }
  }

  debounceFormSubmit(form) {
    this.loadingIndicator.style.display = 'block';
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      form.submit();
    }, 1000);
  }
}

if (!customElements.get('carat-weight-range')) {
  customElements.define('carat-weight-range', CaratWeightRange);
}

// Treatment filter value consolidation
class TreatmentFilterConsolidator {
  constructor() {
    this.valueMapping = {
      // English -> German mappings
      'none': 'keine',
      'heated': 'erhitzt', 
      'natural': 'natürlich',
      'treated': 'behandelt',
      'untreated': 'unbehandelt',
      // Add more mappings as needed
    };
    
    this.init();
  }
  
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.consolidateFilters());
    } else {
      this.consolidateFilters();
    }
    
    // Also run after AJAX updates (for facet changes)
    document.addEventListener('facets:updated', () => {
      setTimeout(() => this.consolidateFilters(), 100);
    });
    
    // Fallback: run periodically to catch any missed updates
    setInterval(() => this.consolidateFilters(), 2000);
  }
  
  consolidateFilters() {
    // Find treatment filter sections
    const treatmentFilters = document.querySelectorAll('.accordion-list .accordion');
    
    treatmentFilters.forEach(filter => {
      const title = filter.querySelector('.accordion__title')?.textContent?.toLowerCase();
      
      if (title && (title.includes('treatment') || title.includes('behandlung'))) {
        this.consolidateTreatmentValues(filter);
      }
    });
  }
  
  consolidateTreatmentValues(filterElement) {
    const checkboxes = filterElement.querySelectorAll('input[type="checkbox"]');
    const labels = filterElement.querySelectorAll('label');
    const valuesToHide = new Set();
    
    // Group values by their meaning
    const valueGroups = {};
    
    labels.forEach((label, index) => {
      const text = label.textContent.trim().toLowerCase();
      const checkbox = checkboxes[index];
      
      if (!checkbox) return;
      
      // Find if this value has a mapping
      let groupKey = text;
      for (const [en, de] of Object.entries(this.valueMapping)) {
        if (text.includes(en.toLowerCase()) || text.includes(de.toLowerCase())) {
          groupKey = de.toLowerCase(); // Use German as the canonical form
          break;
        }
      }
      
      if (!valueGroups[groupKey]) {
        valueGroups[groupKey] = [];
      }
      
      valueGroups[groupKey].push({
        checkbox,
        label,
        text,
        originalText: text
      });
    });
    
    // Hide duplicate values and consolidate
    Object.values(valueGroups).forEach(group => {
      if (group.length > 1) {
        // Keep the first one (or prefer German), hide others
        const preferred = group.find(item => 
          Object.values(this.valueMapping).some(de => 
            item.text.includes(de.toLowerCase())
          )
        ) || group[0];
        
        // Combine counts if shown
        let totalCount = 0;
        group.forEach(item => {
          const countMatch = item.text.match(/\((\d+)\)/);
          if (countMatch) {
            totalCount += parseInt(countMatch[1]);
          }
        });
        
        // Update preferred label with combined count
        if (totalCount > 0) {
          const baseText = preferred.text.replace(/\s*\(\d+\)/, '');
          preferred.label.textContent = `${baseText} (${totalCount})`;
        }
        
        // Hide duplicates
        group.forEach(item => {
          if (item !== preferred) {
            item.checkbox.style.display = 'none';
            item.label.style.display = 'none';
            
            // Transfer any active state to preferred
            if (item.checkbox.checked && !preferred.checkbox.checked) {
              preferred.checkbox.checked = true;
            }
          }
        });
      }
    });
  }
}

// Initialize the consolidator
new TreatmentFilterConsolidator();